/**
 * Eine Projektkarte der Übersicht (Kachelansicht am Rechner) — bis 13.10.2026
 * `renderGroupCard` in ProjektePage. Der Statuswechsel sitzt in der Karte; die
 * Karte selbst ist kein Bedienelement, Tastaturziel ist der Link im Titel.
 */
import { Link, useNavigate } from "react-router-dom";
import type { ProjektUebersicht } from "../../lib/types";
import type { ProjektPhaseZeile } from "../../lib/projektLage";
import {
  PFLEGE_STATUS_ORDER,
  canonicalProjektStatus,
  computeGewinn,
  fmtEUR,
  initialsFromName,
  statusIdFor,
} from "../../lib/projekte";

export type StatusWahl = {
  optionen: string[];
  /** Läuft gerade ein Wechsel für dieses Projekt? */
  wechseltGerade: (id: string) => boolean;
  aendern: (id: string, neu: string) => void;
};

/** Pflege bietet nur den reduzierten Status-Satz; der aktuelle Wert bleibt
 *  immer wählbar, falls ein Altprojekt einen Bau-Status trägt. */
export function statusAuswahl(p: ProjektUebersicht, optionen: string[]) {
  const kanon = canonicalProjektStatus(p.status, optionen);
  const aktuell = optionen.includes(kanon) ? kanon : optionen[0] ?? kanon;
  const liste = p.projektart === "pflege"
    ? optionen.filter((s) => PFLEGE_STATUS_ORDER.includes(s) || s === aktuell)
    : optionen;
  return { kanon, aktuell, liste, sid: statusIdFor(kanon) };
}

export function StatusPille({ p, wahl }: { p: ProjektUebersicht; wahl: StatusWahl }) {
  const { aktuell, liste, sid } = statusAuswahl(p, wahl.optionen);
  return (
    <div className={"vg-proj-card-status s-" + sid} onClick={(e) => e.stopPropagation()} title="Status ändern">
      <select
        aria-label={`Status für ${p.title || "Projekt"}`}
        value={aktuell}
        disabled={wahl.wechseltGerade(p.id) || wahl.optionen.length === 0}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => { e.stopPropagation(); wahl.aendern(p.id, e.target.value); }}
      >
        {liste.length === 0
          ? <option value={aktuell}>{aktuell}</option>
          : liste.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    </div>
  );
}

export default function ProjektKarte({ p, phase, wahl }: { p: ProjektUebersicht; phase?: ProjektPhaseZeile; wahl: StatusWahl }) {
  const navigate = useNavigate();
  const { sid } = statusAuswahl(p, wahl.optionen);
  const gewinn = computeGewinn(p);
  const netto = Number(p.nettowert || 0);
  const initials = p.verantwortlicher ? initialsFromName(p.verantwortlicher) : "";
  const prio = (p.prioritaet || "").toLowerCase();
  return (
    <article className="vg-proj-card" onClick={() => navigate(`/projekte/${p.id}`)}>
      <span className={"vg-proj-card-side s-" + sid} />
      <div className="vg-proj-card-head">
        <div className="vg-proj-card-titlewrap">
          <div className="vg-proj-card-title">
            <Link to={`/projekte/${p.id}`} onClick={(e) => e.stopPropagation()}>{p.title || "—"}</Link>
          </div>
          <div className={"vg-proj-card-customer" + (p.kunde_name ? "" : " is-empty")}>
            {p.kunde_name || "— ohne Kunde"}
          </div>
        </div>
        {netto > 0 && (
          <div>
            <div className="vg-proj-card-amount">{fmtEUR(netto)}</div>
            {gewinn > 0 && <div className="vg-proj-card-amount-sub">+{fmtEUR(gewinn)}</div>}
          </div>
        )}
      </div>
      <div className="vg-proj-card-meta">
        {p.ort && <span>📍 {p.ort}</span>}
        {p.ort && p.verantwortlicher && <span className="sep">·</span>}
        {p.verantwortlicher && <span>{initials} {p.verantwortlicher}</span>}
      </div>
      {p.projektart !== "pflege" && phase && (
        <div className="vg-proj-card-lage">
          {phase.schritt && <span className="vg-proj-card-schritt">→ {phase.schritt.text}</span>}
          {phase.ueberfaellig && <span className="ph-marke is-rot">überfällig</span>}
          <span className="vg-proj-card-grund">{phase.grund}</span>
        </div>
      )}
      <div className="vg-proj-card-foot">
        {p.projektart === "bau" && <span className="vg-proj-tag is-bau">Bau</span>}
        {p.projektart === "pflege" && <span className="vg-proj-tag is-pflege">Pflege</span>}
        {p.hat_angebot && <span className="vg-proj-tag is-angebot">📄 Angebot</span>}
        {p.hat_rechnung && <span className="vg-proj-tag is-rechnung">🧾 Rechnung</span>}
        {prio === "dringend" && <span className="vg-proj-tag is-prio-dringend">⚠ Dringend</span>}
        {prio === "hoch" && <span className="vg-proj-tag is-prio-hoch">↑ Hoch</span>}
        <StatusPille p={p} wahl={wahl} />
      </div>
    </article>
  );
}
